import { clsx } from "clsx";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
	CalendarDays,
	CalendarPlus,
	CalendarRange,
	ChevronLeft,
	ChevronRight,
	MapPin,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ViewToggle } from "@/components/view-toggle";
import { useAttendanceDays } from "@/features/presence/hooks/use-attendance-days";
import { useNow } from "@/features/presence/hooks/use-now";
import type {
	AttendanceDay,
	AttendanceWorkerDays,
	PeriodKind,
} from "@/features/presence/types";
import {
	ATTACHMENT_ICON,
	allEntries,
	cellMeta,
	dayWorkedMinutes,
	findDay,
	hasFarPunch,
	isFutureDate,
	sumTotals,
} from "@/features/presence/utils/attendance";
import { hasOpenEntry } from "@/features/presence/utils/day";
import {
	EMPTY_VALUE,
	formatMinutes,
	formatSignedMinutes,
} from "@/features/presence/utils/format";
import {
	isCurrentPeriod,
	periodDates,
	periodLabel,
	periodRange,
	shiftPeriod,
} from "@/features/presence/utils/period";
import { currentWorkDate, currentWorkDay, dateToDateOnly } from "@/lib/format";
import { activeWorkers, useWorkers } from "../../hooks/use-workers";
import { DayEditor } from "../day-editor";
import { TimeEntryDialog } from "../time-entry-dialog";

const PERIOD_OPTIONS = [
	{ value: "week" as const, label: "Semana", icon: CalendarDays },
	{ value: "month" as const, label: "Mês", icon: CalendarRange },
];

const NAV_LABELS: Record<PeriodKind, { previous: string; next: string }> = {
	week: { previous: "Semana anterior", next: "Próxima semana" },
	month: { previous: "Mês anterior", next: "Próximo mês" },
};

interface Selection {
	workerId: string;
	date: string;
}

function weekdayLabel(date: Date): string {
	return format(date, "EEEEEE", { locale: ptBR });
}

function cellLabel(name: string, date: Date, status: string): string {
	return `Dia ${format(date, "dd/MM")} de ${name}: ${status}`;
}

function CellContent({ day, now }: { day: AttendanceDay; now: Date }) {
	const meta = cellMeta(day);
	const Icon = meta.icon;
	const Clip = ATTACHMENT_ICON;
	const far = hasFarPunch(day);
	const showMarks = meta.hasAttachments || far || (Icon && meta.showTotal);
	return (
		<>
			<span className="attendance-cell__total">
				{meta.showTotal && formatMinutes(dayWorkedMinutes(day, now))}
				{!meta.showTotal && Icon && <Icon aria-hidden="true" />}
				{!meta.showTotal && !Icon && EMPTY_VALUE}
			</span>
			{day.totals.overtimeMinutes > 0 && (
				<span className="attendance-cell__overtime">
					{formatSignedMinutes(day.totals.overtimeMinutes)}
				</span>
			)}
			{showMarks && (
				<span className="attendance-cell__marks">
					{Icon && meta.showTotal && <Icon aria-hidden="true" />}
					{far && (
						<MapPin
							className="attendance-cell__far"
							aria-label="Ponto longe do canteiro"
						/>
					)}
					{meta.hasAttachments && (
						<Clip className="attendance-cell__clip" aria-label="Com anexo" />
					)}
				</span>
			)}
		</>
	);
}

export function AttendanceSheet({
	canUpdate,
	period,
	reference,
	focusDate,
	onPeriodChange,
	onReferenceChange,
}: {
	canUpdate: boolean;
	period: PeriodKind;
	reference: Date;
	focusDate?: string;
	onPeriodChange: (period: PeriodKind) => void;
	onReferenceChange: (reference: Date) => void;
}) {
	const [selection, setSelection] = useState<Selection | undefined>();
	const [editorOpen, setEditorOpen] = useState(false);
	const [entryDialogOpen, setEntryDialogOpen] = useState(false);
	const gridRef = useRef<HTMLTableElement>(null);

	const range = periodRange(period, reference);
	const dates = periodDates(period, reference);
	const daysQuery = useAttendanceDays(range);
	const { data: workers } = useWorkers();
	const items = daysQuery.data ?? [];
	const now = useNow(hasOpenEntry(allEntries(items)));
	const today = currentWorkDate();

	const flaggedWorkers = activeWorkers(workers).filter(
		(worker) => worker.timeClockEnabled,
	);
	const selectedItem = selection
		? items.find((item) => item.worker.id === selection.workerId)
		: undefined;
	const selectedDay = selection
		? findDay(items, selection.workerId, selection.date)
		: undefined;

	const hasGrid = daysQuery.isSuccess && items.length > 0;

	useEffect(() => {
		if (!focusDate || !hasGrid) return;
		gridRef.current
			?.querySelector(`th[data-work-date="${focusDate}"]`)
			?.scrollIntoView({ block: "center", inline: "center" });
	}, [focusDate, hasGrid]);

	function openDay(item: AttendanceWorkerDays, day: AttendanceDay) {
		setSelection({ workerId: item.worker.id, date: day.date });
		setEditorOpen(true);
	}

	return (
		<Card className="attendance-sheet">
			<CardHeader>
				<CardTitle>Folha de presença</CardTitle>
				<CardDescription className="attendance-sheet__period">
					{periodLabel(period, reference)}
				</CardDescription>
				<CardAction>
					{canUpdate && (
						<Button variant="outline" onClick={() => setEntryDialogOpen(true)}>
							<CalendarPlus />
							Registrar dia
						</Button>
					)}
				</CardAction>
			</CardHeader>

			<CardContent className="attendance-sheet__content">
				<div className="attendance-sheet__toolbar">
					<ViewToggle
						options={PERIOD_OPTIONS}
						value={period}
						onChange={onPeriodChange}
					/>
					<div className="attendance-sheet__nav">
						<Button
							variant="outline"
							size="icon"
							aria-label={NAV_LABELS[period].previous}
							onClick={() =>
								onReferenceChange(shiftPeriod(period, reference, -1))
							}
						>
							<ChevronLeft />
						</Button>
						<Button
							variant="outline"
							size="icon"
							aria-label={NAV_LABELS[period].next}
							disabled={isCurrentPeriod(period, reference)}
							onClick={() =>
								onReferenceChange(shiftPeriod(period, reference, 1))
							}
						>
							<ChevronRight />
						</Button>
					</div>
					<div className="attendance-sheet__date">
						<DatePicker
							aria-label="Ir para a data"
							selected={reference}
							onSelected={(next) => onReferenceChange(next ?? currentWorkDay())}
							calendarProps={{ disabled: { after: currentWorkDay() } }}
						/>
					</div>
				</div>

				{daysQuery.isPending && (
					<div className="attendance-sheet__skeleton">
						{[0, 1, 2].map((row) => (
							<Skeleton key={row} className="attendance-sheet__skeleton-row" />
						))}
					</div>
				)}

				{daysQuery.isError && (
					<p className="attendance-sheet__state is-error">
						Não foi possível carregar a folha de presença.
					</p>
				)}

				{hasGrid && (
					<table ref={gridRef} className="attendance-grid" data-period={period}>
						<thead>
							<tr>
								<th className="attendance-grid__worker">Pessoa</th>
								{dates.map((date) => {
									const workDate = dateToDateOnly(date);
									return (
										<th
											key={workDate}
											className="attendance-grid__day"
											data-work-date={workDate}
											data-today={workDate === today ? "true" : undefined}
											data-focus={workDate === focusDate ? "true" : undefined}
										>
											<span className="attendance-grid__weekday">
												{weekdayLabel(date)}
											</span>
											<span className="attendance-grid__day-number">
												{format(date, "dd")}
											</span>
										</th>
									);
								})}
								<th className="attendance-grid__summary">Total</th>
							</tr>
						</thead>
						<tbody>
							{items.map((item) => {
								const byDate = new Map(item.days.map((day) => [day.date, day]));
								const totals = sumTotals(item.days);
								return (
									<tr key={item.worker.id} data-worker-id={item.worker.id}>
										<th scope="row" className="attendance-grid__worker">
											<span className="attendance-grid__name">
												{item.worker.name}
											</span>
											{item.worker.jobTitle && (
												<span className="attendance-grid__job">
													{item.worker.jobTitle}
												</span>
											)}
										</th>
										{dates.map((date) => {
											const workDate = dateToDateOnly(date);
											const day = byDate.get(workDate);
											const focus = workDate === focusDate ? "true" : undefined;
											if (!day) {
												return (
													<td
														key={workDate}
														className="attendance-grid__cell"
														data-work-date={workDate}
														data-focus={focus}
													/>
												);
											}
											const future = isFutureDate(day.date, today);
											return (
												<td
													key={workDate}
													className="attendance-grid__cell"
													data-work-date={workDate}
													data-status={day.status}
													data-kind={day.kind}
													data-today={workDate === today ? "true" : undefined}
													data-focus={focus}
												>
													<button
														type="button"
														className={clsx(
															"attendance-cell",
															hasOpenEntry(day.entries) && "is-open",
														)}
														data-status={day.status}
														disabled={future}
														aria-label={cellLabel(
															item.worker.name,
															date,
															cellMeta(day).label,
														)}
														onClick={() => openDay(item, day)}
													>
														<CellContent day={day} now={now} />
													</button>
												</td>
											);
										})}
										<td className="attendance-grid__summary">
											<span className="attendance-grid__summary-total">
												{formatMinutes(totals.workedMinutes)}
											</span>
											{totals.overtimeMinutes > 0 && (
												<span className="attendance-grid__summary-overtime">
													{formatSignedMinutes(totals.overtimeMinutes)}
												</span>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}

				{daysQuery.isSuccess && items.length === 0 && (
					<p className="attendance-sheet__state">
						Nenhuma pessoa com bate ponto ativo neste canteiro.
					</p>
				)}
			</CardContent>

			<DayEditor
				open={editorOpen}
				onOpenChange={setEditorOpen}
				worker={selectedItem?.worker}
				day={selectedDay}
				canUpdate={canUpdate}
			/>

			<TimeEntryDialog
				open={entryDialogOpen}
				onOpenChange={setEntryDialogOpen}
				mode="create"
				workers={flaggedWorkers}
			/>
		</Card>
	);
}

export function AttendanceSheetSkeleton() {
	return (
		<Card className="attendance-sheet">
			<CardHeader>
				<Skeleton className="attendance-sheet__skeleton-title" />
			</CardHeader>
			<CardContent className="attendance-sheet__content">
				<div className="attendance-sheet__skeleton">
					{[0, 1, 2].map((row) => (
						<Skeleton key={row} className="attendance-sheet__skeleton-row" />
					))}
				</div>
			</CardContent>
		</Card>
	);
}
